import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  EllipsisHorizontalCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import toast from "../utils/toast";
import { apiService } from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";
import { Navigate } from "react-router-dom";

type CalendarState = {
  holidays: Set<string>;
  workdayOverrides: Set<string>;
  halfDays: Set<string>;
};

type CompanySettingsResponse = {
  workHours?: {
    dailyTargetHours?: number;
    overtimeMultiplier?: number;
  };
  taxes?: {
    vatRate?: number;
    corporateTaxRate?: number;
  };
  workCalendar?: {
    holidaysByYear?: Record<string, string[]>;
    workdayOverridesByYear?: Record<string, string[]>;
    halfDaysByYear?: Record<string, string[]>;
  };
  activityTableLayouts?: {
    manager_dashboard_activities?: Array<{
      id?: string;
      kind?: string;
      label?: string;
    }>;
  };
  whatsappReportBot?: {
    enabled?: boolean;
    recipients?: string[];
    sendTime?: string;
    selectedActivityColumns?: string[];
  };
};

type UserHourlyCost = {
  id: number;
  firstName: string;
  lastName: string;
  username?: string;
  email?: string;
  role?: string;
  hourlyCost?: number | null;
  currency?: string | null;
};

type UserHourlyCostRate = {
  id?: number;
  userId: number;
  startDate: string;
  endDate?: string | null;
  hourlyCost: number | null;
  currency: string;
};

type CompanyHourlyCostRate = {
  id?: number;
  startDate: string;
  endDate?: string | null;
  hourlyCost: number | null;
  currency: string;
};

type DailyExchangeRateRow = {
  id?: number;
  rateDate: string;
  baseCurrency: string;
  provider?: string | null;
  rates: Record<string, number | null>;
};

type WhatsAppActivityColumnOption = {
  id: string;
  label: string;
};

type CurrencyOption = {
  code: string;
  label: string;
  modalLabel: string;
};

const PROJECT_CURRENCY_OPTIONS: CurrencyOption[] = [
  { code: "TRY", label: "TRY - TL", modalLabel: "₺ - TRY (Turkish Lira)" },
  { code: "USD", label: "USD - Dollar", modalLabel: "$ - USD (US Dollar)" },
  { code: "EUR", label: "EUR - Euro", modalLabel: "€ - EUR (Euro)" },
  { code: "GBP", label: "GBP - Pound", modalLabel: "£ - GBP (British Pound)" },
  { code: "AZN", label: "AZN - Manat", modalLabel: "₼ - AZN (Azerbaijani Manat)" },
  { code: "CHF", label: "CHF - Franc", modalLabel: "CHF - CHF (Swiss Franc)" },
  { code: "JPY", label: "JPY - Yen", modalLabel: "¥ - JPY (Japanese Yen)" },
  { code: "CNY", label: "CNY - Yuan", modalLabel: "¥ - CNY (Chinese Yuan)" },
  { code: "RUB", label: "RUB - Ruble", modalLabel: "₽ - RUB (Russian Ruble)" },
  { code: "SAR", label: "SAR - Riyal", modalLabel: "﷼ - SAR (Saudi Riyal)" },
  { code: "AED", label: "AED - Dirham", modalLabel: "د.إ - AED (UAE Dirham)" },
];

const HOURLY_COST_CURRENCIES = PROJECT_CURRENCY_OPTIONS.slice(0, 5).map(
  ({ code, label }) => ({ code, label })
);

const DAILY_EXCHANGE_RATE_VISIBLE_CODES = ["USD", "EUR", "GBP", "AZN"];
const DAILY_EXCHANGE_RATE_DEFAULT_CURRENCIES = PROJECT_CURRENCY_OPTIONS.filter(
  (option) => option.code !== "TRY"
);

const createEmptyDailyExchangeRateRates = (): Record<string, number | null> =>
  DAILY_EXCHANGE_RATE_DEFAULT_CURRENCIES.reduce<Record<string, number | null>>(
    (acc, option) => {
      acc[option.code] = null;
      return acc;
    },
    {}
  );

const WHATSAPP_ACTIVITY_COLUMN_OPTIONS: WhatsAppActivityColumnOption[] = [
  { id: "rowNo", label: "Sıra No" },
  { id: "endDate", label: "Bitiş Tarihi" },
  { id: "assignee", label: "Çalışan Kişi" },
  { id: "startTime", label: "Başlangıç Saati" },
  { id: "endTimeOnly", label: "Bitiş Saati" },
  { id: "duration", label: "Çalışılan Süre" },
  { id: "cost", label: "Maliyet (TL)" },
  { id: "projectName", label: "Çalışılan Proje" },
  { id: "formContent", label: "Yapılan İş" },
  { id: "submitDescription", label: "İş Detayı" },
  { id: "managerComment", label: "Yönetici Yorumu" },
];

const createEmptyDailyExchangeRateRow = (
  rateDate: string
): DailyExchangeRateRow => ({
  rateDate,
  baseCurrency: "TRY",
  provider: "MANUAL",
  rates: createEmptyDailyExchangeRateRates(),
});

const buildDateKey = (year: number, monthIndex: number, day: number): string => {
  const month = String(monthIndex + 1).padStart(2, "0");
  const date = String(day).padStart(2, "0");
  return `${year}-${month}-${date}`;
};

const formatLocalISODate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatMonthValue = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const parseMonthValue = (
  monthValue: string
): { year: number; monthIndex: number } => {
  const match = /^(\d{4})-(\d{2})$/.exec(monthValue);
  if (match) {
    const year = Number(match[1]);
    const monthNumber = Number(match[2]);
    if (Number.isFinite(year) && monthNumber >= 1 && monthNumber <= 12) {
      return { year, monthIndex: monthNumber - 1 };
    }
  }

  const today = new Date();
  return {
    year: today.getFullYear(),
    monthIndex: today.getMonth(),
  };
};

const shiftMonthValue = (monthValue: string, diff: number): string => {
  const { year, monthIndex } = parseMonthValue(monthValue);
  return formatMonthValue(new Date(year, monthIndex + diff, 1));
};

const getMonthRange = (
  monthValue: string
): { startDate: string; endDate: string } => {
  const { year, monthIndex } = parseMonthValue(monthValue);
  const startDate = formatLocalISODate(new Date(year, monthIndex, 1));
  const today = new Date();
  const isCurrentMonth =
    year === today.getFullYear() && monthIndex === today.getMonth();
  const endDate = isCurrentMonth
    ? formatLocalISODate(today)
    : formatLocalISODate(new Date(year, monthIndex + 1, 0));

  return { startDate, endDate };
};

const getMonthLabel = (year: number, monthIndex: number): string => {
  return new Date(year, monthIndex, 1).toLocaleDateString("tr-TR", {
    month: "long",
  });
};

const getMonthDays = (year: number, monthIndex: number): (number | null)[] => {
  const firstDay = new Date(year, monthIndex, 1);
  const startIndex = (firstDay.getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const cells: (number | null)[] = [];

  for (let i = 0; i < startIndex; i += 1) {
    cells.push(null);
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(day);
  }

  return cells;
};

const isWeekendDay = (year: number, monthIndex: number, day: number): boolean => {
  const weekday = new Date(year, monthIndex, day).getDay();
  return weekday === 0 || weekday === 6;
};

export const CompanySettings: React.FC = () => {
  const { t, i18n } = useTranslation(["settings", "common", "errors"]);
  const { isAdmin, isManager } = useAuth();

  const [settings, setSettings] = useState<CompanySettingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingHours, setSavingHours] = useState(false);
  const [savingTaxes, setSavingTaxes] = useState(false);
  const [savingCalendar, setSavingCalendar] = useState(false);
  const [savingWhatsAppBot, setSavingWhatsAppBot] = useState(false);
  const [sendingWhatsAppReport, setSendingWhatsAppReport] = useState(false);
  const [sendingWhatsAppTest, setSendingWhatsAppTest] = useState(false);
  const [dailyTargetHours, setDailyTargetHours] = useState(9);
  const [overtimeMultiplier, setOvertimeMultiplier] = useState(1.5);
  const [vatRate, setVatRate] = useState(20);
  const [corporateTaxRate, setCorporateTaxRate] = useState(25);
  const [activeSection, setActiveSection] = useState<
    "daily" | "calendar" | "hourly" | "taxes" | "exchange" | "whatsapp"
  >("daily");
  const [whatsAppBotEnabled, setWhatsAppBotEnabled] = useState(false);
  const [whatsAppRecipientsInput, setWhatsAppRecipientsInput] = useState("");
  const [whatsAppSendTime, setWhatsAppSendTime] = useState("18:00");
  const [selectedWhatsAppColumns, setSelectedWhatsAppColumns] = useState<
    string[]
  >(
    WHATSAPP_ACTIVITY_COLUMN_OPTIONS.filter((option) => option.id !== "cost").map(
      (option) => option.id
    )
  );
  const [hourlyCosts, setHourlyCosts] = useState<UserHourlyCost[]>([]);
  const [hourlyCostsLoading, setHourlyCostsLoading] = useState(false);

  const [hourlyRateModalUser, setHourlyRateModalUser] =
    useState<UserHourlyCost | null>(null);
  const [hourlyRates, setHourlyRates] = useState<UserHourlyCostRate[]>([]);
  const [hourlyRatesLoading, setHourlyRatesLoading] = useState(false);
  const [savingHourlyRates, setSavingHourlyRates] = useState(false);
  const [deletedHourlyRateIds, setDeletedHourlyRateIds] = useState<number[]>([]);
  const [averageRateModalOpen, setAverageRateModalOpen] = useState(false);
  const [averageHourlyRates, setAverageHourlyRates] = useState<
    CompanyHourlyCostRate[]
  >([]);
  const [averageHourlyRatesLoading, setAverageHourlyRatesLoading] =
    useState(false);
  const [savingAverageHourlyRates, setSavingAverageHourlyRates] =
    useState(false);
  const [deletedAverageHourlyRateIds, setDeletedAverageHourlyRateIds] =
    useState<number[]>([]);
  const [dailyExchangeRates, setDailyExchangeRates] = useState<
    DailyExchangeRateRow[]
  >([]);
  const [dailyExchangeRatesLoading, setDailyExchangeRatesLoading] =
    useState(false);
  const [savingDailyExchangeRateKey, setSavingDailyExchangeRateKey] =
    useState<string | null>(null);
  const [deletingDailyExchangeRateKey, setDeletingDailyExchangeRateKey] =
    useState<string | null>(null);
  const [dailyExchangeRateModalIndex, setDailyExchangeRateModalIndex] =
    useState<number | null>(null);
  const [dailyExchangeRateModalSearch, setDailyExchangeRateModalSearch] =
    useState("");

  const currentYear = new Date().getFullYear();
  const currentExchangeMonthValue = useMemo(
    () => formatMonthValue(new Date()),
    []
  );
  const [selectedExchangeMonth, setSelectedExchangeMonth] = useState(
    currentExchangeMonthValue
  );
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [calendarState, setCalendarState] = useState<CalendarState>({
    holidays: new Set<string>(),
    workdayOverrides: new Set<string>(),
    halfDays: new Set<string>(),
  });

  const canView = isAdmin() || isManager();
  const { startDate: dailyExchangeRangeStart, endDate: dailyExchangeRangeEnd } =
    useMemo(
      () => getMonthRange(selectedExchangeMonth),
      [selectedExchangeMonth]
    );
  const selectedExchangeMonthLabel = useMemo(() => {
    const { year, monthIndex } = parseMonthValue(selectedExchangeMonth);
    return new Date(year, monthIndex, 1).toLocaleDateString(
      i18n.resolvedLanguage || i18n.language || "tr-TR",
      {
        month: "long",
        year: "numeric",
      }
    );
  }, [i18n.language, i18n.resolvedLanguage, selectedExchangeMonth]);
  const selectedWhatsAppColumnLabels = useMemo(
    () =>
      WHATSAPP_ACTIVITY_COLUMN_OPTIONS.filter((option) =>
        selectedWhatsAppColumns.includes(option.id)
      ).map((option) => option.label),
    [selectedWhatsAppColumns]
  );
  const knownDailyCurrencyByCode = useMemo(
    () =>
      DAILY_EXCHANGE_RATE_DEFAULT_CURRENCIES.reduce<Record<string, CurrencyOption>>(
        (acc, option) => {
          acc[option.code] = option;
          return acc;
        },
        {}
      ),
    []
  );
  const dailyExchangeRateCurrencyOptions = useMemo(() => {
    const seen = new Set(
      DAILY_EXCHANGE_RATE_DEFAULT_CURRENCIES.map((option) => option.code)
    );
    const extras: CurrencyOption[] = [];

    dailyExchangeRates.forEach((row) => {
      Object.keys(row.rates || {}).forEach((code) => {
        const normalizedCode = String(code || "").trim().toUpperCase();
        if (!normalizedCode || normalizedCode === "TRY" || seen.has(normalizedCode)) {
          return;
        }
        seen.add(normalizedCode);
        extras.push({
          code: normalizedCode,
          label: `${normalizedCode} - ${normalizedCode}`,
          modalLabel: normalizedCode,
        });
      });
    });

    return [...DAILY_EXCHANGE_RATE_DEFAULT_CURRENCIES, ...extras];
  }, [dailyExchangeRates]);
  const visibleDailyExchangeRateCurrencies = useMemo(
    () =>
      DAILY_EXCHANGE_RATE_VISIBLE_CODES.map(
        (code) => knownDailyCurrencyByCode[code]
      ).filter((option): option is CurrencyOption => Boolean(option)),
    [knownDailyCurrencyByCode]
  );
  const hiddenDailyExchangeRateCurrencies = useMemo(
    () =>
      dailyExchangeRateCurrencyOptions.filter(
        (option) => !DAILY_EXCHANGE_RATE_VISIBLE_CODES.includes(option.code)
      ),
    [dailyExchangeRateCurrencyOptions]
  );
  const activeDailyExchangeRateRow =
    dailyExchangeRateModalIndex !== null
      ? dailyExchangeRates[dailyExchangeRateModalIndex] || null
      : null;
  const filteredDailyExchangeRateCurrencyOptions = useMemo(() => {
    const normalizedSearch = dailyExchangeRateModalSearch.trim().toLowerCase();
    if (!normalizedSearch) {
      return dailyExchangeRateCurrencyOptions;
    }

    return dailyExchangeRateCurrencyOptions.filter((option) => {
      const haystack = `${option.code} ${option.label} ${option.modalLabel}`.toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [dailyExchangeRateCurrencyOptions, dailyExchangeRateModalSearch]);

  const getRoleLabel = (role?: string | null): string => {
    if (!role) return "-";
    const camelCaseRole = role
      .toLowerCase()
      .replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    const translationKey = `common:roles.${camelCaseRole}`;
    const translated = t(translationKey);
    return translated !== translationKey ? translated : role;
  };

  const parseWhatsAppRecipients = useCallback((value: string): string[] => {
    return Array.from(
      new Set(
        value
          .split(/[\n,;]/)
          .map((item) => item.trim())
          .filter(Boolean)
      )
    );
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getCompanySettings();
      const data = (response?.data || response) as CompanySettingsResponse | null;
      setSettings(data || {});
        const hours = data?.workHours?.dailyTargetHours;
        if (typeof hours === "number" && hours > 0) {
          setDailyTargetHours(hours);
        }
        const fetchedOvertimeMultiplier = data?.workHours?.overtimeMultiplier;
        if (
          typeof fetchedOvertimeMultiplier === "number" &&
          fetchedOvertimeMultiplier > 0
        ) {
          setOvertimeMultiplier(fetchedOvertimeMultiplier);
        } else {
          setOvertimeMultiplier(1.5);
        }
      const fetchedVatRate = data?.taxes?.vatRate;
      if (typeof fetchedVatRate === "number" && fetchedVatRate >= 0) {
        setVatRate(fetchedVatRate);
      } else {
        setVatRate(20);
      }
      const fetchedCorporateTaxRate = data?.taxes?.corporateTaxRate;
      if (
        typeof fetchedCorporateTaxRate === "number" &&
        fetchedCorporateTaxRate >= 0
      ) {
        setCorporateTaxRate(fetchedCorporateTaxRate);
      } else {
        setCorporateTaxRate(25);
      }

      const rawStoredLayoutColumns =
        data?.activityTableLayouts?.manager_dashboard_activities;
      const storedLayoutColumns = Array.isArray(rawStoredLayoutColumns)
        ? rawStoredLayoutColumns
            .map((column) =>
              typeof column?.id === "string" ? column.id.trim() : ""
            )
            .filter((id): id is string => Boolean(id))
        : [];
      const botSettings = data?.whatsappReportBot;
      const rawSelectedColumns = botSettings?.selectedActivityColumns;
      const selectedColumns = Array.isArray(rawSelectedColumns)
        ? rawSelectedColumns.filter((columnId) =>
            WHATSAPP_ACTIVITY_COLUMN_OPTIONS.some(
              (option) => option.id === columnId
            )
          )
        : [];
      setWhatsAppBotEnabled(Boolean(botSettings?.enabled));
      const rawRecipients = botSettings?.recipients;
      const recipientLines = Array.isArray(rawRecipients)
        ? rawRecipients
        : [];
      setWhatsAppRecipientsInput(recipientLines.join("\n"));
      setWhatsAppSendTime(
        typeof botSettings?.sendTime === "string" &&
          /^\d{2}:\d{2}$/.test(botSettings.sendTime)
          ? botSettings.sendTime
          : "18:00"
      );
      setSelectedWhatsAppColumns(
        selectedColumns.length
          ? selectedColumns
          : storedLayoutColumns.length
            ? storedLayoutColumns.filter((columnId) =>
                WHATSAPP_ACTIVITY_COLUMN_OPTIONS.some(
                  (option) => option.id === columnId
                )
              )
            : WHATSAPP_ACTIVITY_COLUMN_OPTIONS.filter(
                (option) => option.id !== "cost"
              ).map((option) => option.id)
      );
    } catch (error) {
      toast.error(t("errors:generic"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadHourlyCosts = useCallback(async () => {
    try {
      setHourlyCostsLoading(true);
      const response = await apiService.getHourlyCosts();
      const data = (response?.data || response) as UserHourlyCost[] | null;
      setHourlyCosts(
        Array.isArray(data)
          ? data.map((item) => ({
              ...item,
              currency: item.currency || "TRY",
            }))
          : []
      );
    } catch (error) {
      toast.error(t("settings:hourlyCostLoadFailed"));
    } finally {
      setHourlyCostsLoading(false);
    }
  }, [t]);

  const loadDailyExchangeRates = useCallback(async () => {
    try {
      setDailyExchangeRatesLoading(true);
      const response = await apiService.getDailyExchangeRates(
        dailyExchangeRangeStart,
        dailyExchangeRangeEnd,
        "TRY"
      );
      const data = (response?.data || response) as any[] | null;
      const rows = Array.isArray(data)
        ? data
            .map((item) => {
              const rateDate =
                typeof item?.rateDate === "string" ? item.rateDate : "";
              if (!rateDate) {
                return null;
              }

              const ratesSource =
                item?.rates && typeof item.rates === "object" ? item.rates : {};
              const rates = Object.entries(ratesSource).reduce<
                Record<string, number | null>
              >((acc, [currencyCode, rawValue]) => {
                const normalizedCode = currencyCode.trim().toUpperCase();
                if (!normalizedCode || normalizedCode === "TRY") {
                  return acc;
                }
                acc[normalizedCode] =
                  typeof rawValue === "number" && Number.isFinite(rawValue)
                    ? rawValue
                    : null;
                return acc;
              }, createEmptyDailyExchangeRateRates());

              return {
                id:
                  typeof item?.id === "number" && Number.isFinite(item.id)
                    ? item.id
                    : undefined,
                rateDate,
                baseCurrency:
                  typeof item?.baseCurrency === "string" &&
                  item.baseCurrency.trim()
                    ? item.baseCurrency.trim().toUpperCase()
                    : "TRY",
                provider:
                  typeof item?.provider === "string" ? item.provider : "MANUAL",
                rates,
              } satisfies DailyExchangeRateRow;
            })
            .filter(
              (item): item is NonNullable<typeof item> => item !== null
            )
            .sort((a, b) => b.rateDate.localeCompare(a.rateDate))
        : [];
      setDailyExchangeRates(rows);
    } catch (error) {
      toast.error(t("settings:exchangeRateLoadFailed"));
    } finally {
      setDailyExchangeRatesLoading(false);
    }
  }, [dailyExchangeRangeEnd, dailyExchangeRangeStart, t]);

  const syncCalendarState = useCallback(
    (year: number, source: CompanySettingsResponse | null) => {
      const holidays =
        source?.workCalendar?.holidaysByYear?.[String(year)] || [];
      const overrides =
        source?.workCalendar?.workdayOverridesByYear?.[String(year)] || [];
      const halfDays =
        source?.workCalendar?.halfDaysByYear?.[String(year)] || [];

      setCalendarState({
        holidays: new Set(holidays),
        workdayOverrides: new Set(overrides),
        halfDays: new Set(halfDays),
      });
    },
    []
  );

  useEffect(() => {
    if (!canView) {
      return;
    }
    loadSettings();
    loadHourlyCosts();
    loadDailyExchangeRates();
  }, [canView, loadSettings, loadHourlyCosts, loadDailyExchangeRates]);

  useEffect(() => {
    syncCalendarState(selectedYear, settings);
  }, [selectedYear, settings, syncCalendarState]);

  const persistCalendarState = async (
    nextState: CalendarState,
    showToast = true,
    reloadAfterSave = true
  ) => {
    try {
      setSavingCalendar(true);
      await apiService.updateWorkCalendarSettings(
        selectedYear,
        Array.from(nextState.holidays),
        Array.from(nextState.workdayOverrides),
        Array.from(nextState.halfDays)
      );
      if (showToast) {
        toast.success(t("settings:calendarSaved"));
      }
      if (reloadAfterSave) {
        await loadSettings();
      }
    } catch (error) {
      toast.error(t("errors:generic"));
    } finally {
      setSavingCalendar(false);
    }
  };

  const handleToggleDate = (year: number, monthIndex: number, day: number) => {
    const dateKey = buildDateKey(year, monthIndex, day);
    const weekend = isWeekendDay(year, monthIndex, day);

    setCalendarState((prev) => {
      const holidays = new Set(prev.holidays);
      const workdayOverrides = new Set(prev.workdayOverrides);
      const halfDays = new Set(prev.halfDays);
      const isHalfDay = halfDays.has(dateKey);
      const isWorkdayOverride = workdayOverrides.has(dateKey);
      const isHoliday =
        !isHalfDay &&
        (holidays.has(dateKey) || (weekend && !isWorkdayOverride));

      if (isHalfDay) {
        holidays.delete(dateKey);
        halfDays.delete(dateKey);
        if (weekend) {
          workdayOverrides.add(dateKey);
        } else {
          workdayOverrides.delete(dateKey);
        }
      } else if (isHoliday) {
        holidays.delete(dateKey);
        workdayOverrides.delete(dateKey);
        halfDays.add(dateKey);
      } else {
        halfDays.delete(dateKey);
        if (!weekend) {
          holidays.add(dateKey);
        } else {
          workdayOverrides.delete(dateKey);
          holidays.delete(dateKey);
        }
      }

      const nextState = { holidays, workdayOverrides, halfDays };
      void persistCalendarState(nextState, false, false);
      return nextState;
    });
  };

  const saveDailyHours = async () => {
    if (
      Number.isNaN(dailyTargetHours) ||
      dailyTargetHours <= 0 ||
      Number.isNaN(overtimeMultiplier) ||
      overtimeMultiplier <= 0
    ) {
      toast.error(t("settings:workHoursValidation"));
      return;
    }

    try {
      setSavingHours(true);
      await apiService.updateWorkHoursSettings(
        dailyTargetHours,
        overtimeMultiplier
      );
      toast.success(t("settings:workHoursSaved"));
      await loadSettings();
    } catch (error) {
      toast.error(t("errors:generic"));
    } finally {
      setSavingHours(false);
    }
  };

  const saveTaxes = async () => {
    if (
      Number.isNaN(vatRate) ||
      Number.isNaN(corporateTaxRate) ||
      vatRate < 0 ||
      vatRate > 100 ||
      corporateTaxRate < 0 ||
      corporateTaxRate > 100
    ) {
      toast.error(t("settings:taxRatesValidation"));
      return;
    }

    try {
      setSavingTaxes(true);
      await apiService.updateTaxSettings(vatRate, corporateTaxRate);
      toast.success(t("settings:taxSettingsSaved"));
      await loadSettings();
    } catch (error) {
      toast.error(t("settings:taxSettingsSaveFailed"));
    } finally {
      setSavingTaxes(false);
    }
  };

  const addDailyExchangeRateRow = () => {
    const existingDates = new Set(dailyExchangeRates.map((row) => row.rateDate));
    let suggestedDate = dailyExchangeRangeEnd;
    let cursor = new Date(`${dailyExchangeRangeEnd}T00:00:00`);
    const minDate = new Date(`${dailyExchangeRangeStart}T00:00:00`);

    while (cursor >= minDate) {
      const candidate = formatLocalISODate(cursor);
      if (!existingDates.has(candidate)) {
        suggestedDate = candidate;
        break;
      }
      cursor.setDate(cursor.getDate() - 1);
    }

    setDailyExchangeRates((prev) => {
      if (prev.some((row) => row.rateDate === suggestedDate && !row.id)) {
        return prev;
      }
      return [createEmptyDailyExchangeRateRow(suggestedDate), ...prev];
    });
  };

  const updateDailyExchangeRateField = (
    index: number,
    currency: string,
    value: string
  ) => {
    setDailyExchangeRates((prev) =>
      prev.map((row, rowIndex) => {
        if (rowIndex !== index) {
          return row;
        }
        return {
          ...row,
          rates: {
            ...row.rates,
            [currency]:
              value.trim() === ""
                ? null
                : Number.isFinite(Number(value))
                ? Number(value)
                : row.rates[currency] ?? null,
          },
        };
      })
    );
  };

  const updateDailyExchangeRateDate = (index: number, rateDate: string) => {
    setDailyExchangeRates((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              rateDate,
            }
          : row
      )
    );
  };

  const saveDailyExchangeRate = async (row: DailyExchangeRateRow) => {
    if (!row.rateDate) {
      toast.error(t("settings:exchangeRateDateRequired"));
      return false;
    }

    const payload = Object.entries(row.rates).reduce<Record<string, number>>(
      (acc, [currency, value]) => {
        if (typeof value === "number" && Number.isFinite(value) && value > 0) {
          acc[currency] = value;
        }
        return acc;
      },
      {}
    );

    if (Object.keys(payload).length === 0) {
      toast.error(t("settings:exchangeRateRatesRequired"));
      return false;
    }

    try {
      setSavingDailyExchangeRateKey(row.rateDate);
      await apiService.upsertDailyExchangeRate(row.rateDate, payload, "TRY");
      toast.success(t("settings:exchangeRateSaved"));
      await loadDailyExchangeRates();
      return true;
    } catch (error) {
      toast.error(t("settings:exchangeRateSaveFailed"));
      return false;
    } finally {
      setSavingDailyExchangeRateKey(null);
    }
  };

  const openDailyExchangeRateModal = (index: number) => {
    setDailyExchangeRateModalSearch("");
    setDailyExchangeRateModalIndex(index);
  };

  const closeDailyExchangeRateModal = () => {
    if (
      activeDailyExchangeRateRow &&
      savingDailyExchangeRateKey === activeDailyExchangeRateRow.rateDate
    ) {
      return;
    }
    setDailyExchangeRateModalSearch("");
    setDailyExchangeRateModalIndex(null);
  };

  const saveDailyExchangeRateFromModal = async () => {
    if (!activeDailyExchangeRateRow) {
      return;
    }
    const isSaved = await saveDailyExchangeRate(activeDailyExchangeRateRow);
    if (isSaved) {
      setDailyExchangeRateModalSearch("");
      setDailyExchangeRateModalIndex(null);
    }
  };

  const toggleWhatsAppColumn = (columnId: string) => {
    setSelectedWhatsAppColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((item) => item !== columnId)
        : [...prev, columnId]
    );
  };

  const persistWhatsAppBotSettings = async (showSuccessToast = true) => {
    const recipients = parseWhatsAppRecipients(whatsAppRecipientsInput);
    if (whatsAppBotEnabled && recipients.length === 0) {
      toast.error(t("settings:whatsappRecipientsRequired"));
      return false;
    }

    if (!selectedWhatsAppColumns.length) {
      toast.error(t("settings:whatsappColumnsRequired"));
      return false;
    }

    if (!/^\d{2}:\d{2}$/.test(whatsAppSendTime)) {
      toast.error(t("settings:whatsappTimeRequired"));
      return false;
    }

    try {
      setSavingWhatsAppBot(true);
      await apiService.updateWhatsAppReportBotSettings({
        enabled: whatsAppBotEnabled,
        recipients,
        sendTime: whatsAppSendTime,
        selectedActivityColumns: selectedWhatsAppColumns,
      });
      if (showSuccessToast) {
        toast.success(t("settings:whatsappSaved"));
      }
      await loadSettings();
      return true;
    } catch (error) {
      toast.error(t("settings:whatsappSaveFailed"));
      return false;
    } finally {
      setSavingWhatsAppBot(false);
    }
  };

  const saveWhatsAppBotSettings = async () => {
    await persistWhatsAppBotSettings(true);
  };

  const resendWhatsAppReportNow = async () => {
    const persisted = await persistWhatsAppBotSettings(false);
    if (!persisted) {
      return;
    }

    try {
      setSendingWhatsAppReport(true);
      const response = await apiService.resendWhatsAppReportNow();
      toast.success(
        response?.message || t("settings:whatsappResendSuccess")
      );
      await loadSettings();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || t("settings:whatsappResendFailed");
      toast.error(message);
    } finally {
      setSendingWhatsAppReport(false);
    }
  };

  const sendWhatsAppTestMessage = async () => {
    const persisted = await persistWhatsAppBotSettings(false);
    if (!persisted) {
      return;
    }

    try {
      setSendingWhatsAppTest(true);
      const response = await apiService.sendWhatsAppTestMessage();
      toast.success(
        response?.message || t("settings:whatsappTestSuccess")
      );
      await loadSettings();
    } catch (error: any) {
      const message =
        error?.response?.data?.message || t("settings:whatsappTestFailed");
      toast.error(message);
    } finally {
      setSendingWhatsAppTest(false);
    }
  };

  const deleteDailyExchangeRate = async (row: DailyExchangeRateRow) => {
    if (!row.id) {
      setDailyExchangeRates((prev) =>
        prev.filter((item) => item !== row)
      );
      return;
    }

    try {
      setDeletingDailyExchangeRateKey(row.rateDate);
      await apiService.deleteDailyExchangeRate(row.rateDate, "TRY");
      setDailyExchangeRates((prev) =>
        prev.filter((item) => item.rateDate !== row.rateDate)
      );
    } catch (error) {
      toast.error(t("settings:exchangeRateDeleteFailed"));
    } finally {
      setDeletingDailyExchangeRateKey(null);
    }
  };

  const saveCalendar = async () => {
    await persistCalendarState(calendarState, true, true);
  };

  const openHourlyRatesModal = async (user: UserHourlyCost) => {
    setHourlyRateModalUser(user);
    setDeletedHourlyRateIds([]);
    setHourlyRates([]);
    try {
      setHourlyRatesLoading(true);
      const response = await apiService.getHourlyCostRates(user.id);
      const data = (response?.data || response) as UserHourlyCostRate[] | null;
      setHourlyRates(
        Array.isArray(data)
          ? data.map((item) => ({
              ...item,
              userId: item.userId ?? user.id,
              startDate: item.startDate || formatLocalISODate(new Date()),
              endDate: item.endDate ?? null,
              currency: item.currency || user.currency || "TRY",
            }))
          : []
      );
    } catch (error) {
      toast.error(t("settings:hourlyCostPeriodsLoadFailed"));
    } finally {
      setHourlyRatesLoading(false);
    }
  };

  const closeHourlyRatesModal = () => {
    setHourlyRateModalUser(null);
    setHourlyRates([]);
    setDeletedHourlyRateIds([]);
    setHourlyRatesLoading(false);
    setSavingHourlyRates(false);
  };

  const addHourlyRateRow = () => {
    if (!hourlyRateModalUser) return;
    setHourlyRates((prev) => [
      {
        userId: hourlyRateModalUser.id,
        startDate: formatLocalISODate(new Date()),
        endDate: null,
        hourlyCost: null,
        currency: hourlyRateModalUser.currency || "TRY",
      },
      ...prev,
    ]);
  };

  const updateHourlyRateField = (
    index: number,
    field: keyof UserHourlyCostRate,
    value: any
  ) => {
    setHourlyRates((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const removeHourlyRateRow = (index: number) => {
    setHourlyRates((prev) => {
      const row = prev[index];
      if (row?.id) {
        setDeletedHourlyRateIds((ids) => [...ids, row.id as number]);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const saveHourlyRates = async () => {
    if (!hourlyRateModalUser) return;
    try {
      setSavingHourlyRates(true);

      for (const id of deletedHourlyRateIds) {
        await apiService.deleteHourlyCostRate(id);
      }

      const normalizePayload = (row: UserHourlyCostRate) => ({
        startDate: row.startDate,
        endDate: row.endDate ? row.endDate : null,
        hourlyCost:
          typeof row.hourlyCost === "number" && Number.isFinite(row.hourlyCost)
            ? row.hourlyCost
            : row.hourlyCost === null
            ? null
            : null,
        currency: row.currency || "TRY",
      });

      const byStartDateAsc = (a: { startDate: string }, b: { startDate: string }) =>
        (a.startDate || "").localeCompare(b.startDate || "");

      const existingRows = hourlyRates
        .filter((row) => Boolean(row.id))
        .sort(byStartDateAsc);
      const newRows = hourlyRates
        .filter((row) => !row.id)
        .sort(byStartDateAsc);

      // First update existing rows so narrowed/shifted ranges are committed
      // before creating new rows that depend on those changes.
      for (const row of existingRows) {
        await apiService.updateHourlyCostRate(
          row.id as number,
          normalizePayload(row)
        );
      }

      // Then create brand-new rows.
      for (const row of newRows) {
        const payload = {
          ...normalizePayload(row),
        };
        await apiService.createHourlyCostRate(hourlyRateModalUser.id, payload);
      }

      toast.success(t("settings:hourlyCostPeriodsSaved"));
      await openHourlyRatesModal(hourlyRateModalUser);
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("settings:hourlyCostPeriodsSaveFailed");
      toast.error(String(message));
    } finally {
      setSavingHourlyRates(false);
      setDeletedHourlyRateIds([]);
    }
  };

  const openAverageRatesModal = async () => {
    setAverageRateModalOpen(true);
    setDeletedAverageHourlyRateIds([]);
    setAverageHourlyRates([]);
    try {
      setAverageHourlyRatesLoading(true);
      const response = await apiService.getAverageHourlyCostRates();
      const data = (response?.data || response) as CompanyHourlyCostRate[] | null;
      setAverageHourlyRates(
        Array.isArray(data)
          ? data.map((item) => ({
              ...item,
              startDate: item.startDate || formatLocalISODate(new Date()),
              endDate: item.endDate ?? null,
              currency: item.currency || "TRY",
            }))
          : []
      );
    } catch (error) {
      toast.error(t("settings:averageHourlyCostPeriodsLoadFailed"));
    } finally {
      setAverageHourlyRatesLoading(false);
    }
  };

  const closeAverageRatesModal = () => {
    setAverageRateModalOpen(false);
    setAverageHourlyRates([]);
    setDeletedAverageHourlyRateIds([]);
    setAverageHourlyRatesLoading(false);
    setSavingAverageHourlyRates(false);
  };

  const addAverageHourlyRateRow = () => {
    setAverageHourlyRates((prev) => [
      {
        startDate: formatLocalISODate(new Date()),
        endDate: null,
        hourlyCost: null,
        currency: "TRY",
      },
      ...prev,
    ]);
  };

  const updateAverageHourlyRateField = (
    index: number,
    field: keyof CompanyHourlyCostRate,
    value: any
  ) => {
    setAverageHourlyRates((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
  };

  const removeAverageHourlyRateRow = (index: number) => {
    setAverageHourlyRates((prev) => {
      const row = prev[index];
      if (row?.id) {
        setDeletedAverageHourlyRateIds((ids) => [...ids, row.id as number]);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const saveAverageHourlyRates = async () => {
    try {
      setSavingAverageHourlyRates(true);

      for (const id of deletedAverageHourlyRateIds) {
        await apiService.deleteAverageHourlyCostRate(id);
      }

      const normalizePayload = (row: CompanyHourlyCostRate) => ({
        startDate: row.startDate,
        endDate: row.endDate ? row.endDate : null,
        hourlyCost:
          typeof row.hourlyCost === "number" && Number.isFinite(row.hourlyCost)
            ? row.hourlyCost
            : row.hourlyCost === null
            ? null
            : null,
        currency: row.currency || "TRY",
      });

      const byStartDateAsc = (a: { startDate: string }, b: { startDate: string }) =>
        (a.startDate || "").localeCompare(b.startDate || "");

      const existingRows = averageHourlyRates
        .filter((row) => Boolean(row.id))
        .sort(byStartDateAsc);
      const newRows = averageHourlyRates
        .filter((row) => !row.id)
        .sort(byStartDateAsc);

      for (const row of existingRows) {
        await apiService.updateAverageHourlyCostRate(
          row.id as number,
          normalizePayload(row)
        );
      }

      for (const row of newRows) {
        const payload = {
          ...normalizePayload(row),
        };
        await apiService.createAverageHourlyCostRate(payload);
      }

      toast.success(t("settings:averageHourlyCostPeriodsSaved"));
      await openAverageRatesModal();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("settings:averageHourlyCostPeriodsSaveFailed");
      toast.error(String(message));
    } finally {
      setSavingAverageHourlyRates(false);
      setDeletedAverageHourlyRateIds([]);
    }
  };

  const workdayCount = useMemo(() => {
    let totalWorkdays = 0;

    for (let month = 0; month < 12; month += 1) {
      const daysInMonth = new Date(selectedYear, month + 1, 0).getDate();
      for (let day = 1; day <= daysInMonth; day += 1) {
        const dateKey = buildDateKey(selectedYear, month, day);
        const weekend = isWeekendDay(selectedYear, month, day);
        const isHalfDay = calendarState.halfDays.has(dateKey);
        const isOverride = calendarState.workdayOverrides.has(dateKey);
        const isHoliday =
          !isHalfDay &&
          !isOverride &&
          (weekend || calendarState.holidays.has(dateKey));

        if (isHalfDay) {
          totalWorkdays += 0.5;
        } else if (!isHoliday) {
          totalWorkdays += 1;
        }
      }
    }

    return totalWorkdays;
  }, [calendarState, selectedYear]);

  if (!canView) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="app-page bg-gradient-to-br from-slate-50 via-blue-50/40 to-indigo-50 py-10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-2xl p-8 shadow-lg">
            <div className="animate-pulse space-y-4">
              <div className="h-6 bg-slate-200 rounded w-1/3" />
              <div className="h-10 bg-slate-200 rounded" />
              <div className="h-6 bg-slate-200 rounded w-1/2" />
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-2xl p-3 shadow-lg">
              <div className="flex flex-nowrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveSection("daily")}
                  className={`inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border px-2.5 py-2 text-xs font-semibold transition sm:px-3 lg:gap-2 lg:px-4 lg:text-sm ${
                    activeSection === "daily"
                      ? "bg-blue-600 text-white border-transparent shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <ClockIcon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{t("settings:dailyTargetTitle")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection("calendar")}
                  className={`inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border px-2.5 py-2 text-xs font-semibold transition sm:px-3 lg:gap-2 lg:px-4 lg:text-sm ${
                    activeSection === "calendar"
                      ? "bg-rose-600 text-white border-transparent shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <CalendarDaysIcon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{t("settings:calendarTitle")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection("hourly")}
                  className={`inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border px-2.5 py-2 text-xs font-semibold transition sm:px-3 lg:gap-2 lg:px-4 lg:text-sm ${
                    activeSection === "hourly"
                      ? "bg-emerald-600 text-white border-transparent shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <CurrencyDollarIcon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{t("settings:hourlyCostTitle")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection("taxes")}
                  className={`inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border px-2.5 py-2 text-xs font-semibold transition sm:px-3 lg:gap-2 lg:px-4 lg:text-sm ${
                    activeSection === "taxes"
                      ? "bg-amber-600 text-white border-transparent shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <CurrencyDollarIcon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{t("settings:taxSettingsTitle")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection("exchange")}
                  className={`inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border px-2.5 py-2 text-xs font-semibold transition sm:px-3 lg:gap-2 lg:px-4 lg:text-sm ${
                    activeSection === "exchange"
                      ? "bg-violet-600 text-white border-transparent shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <DocumentTextIcon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{t("settings:exchangeRateTitle")}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSection("whatsapp")}
                  className={`inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border px-2.5 py-2 text-xs font-semibold transition sm:px-3 lg:gap-2 lg:px-4 lg:text-sm ${
                    activeSection === "whatsapp"
                      ? "bg-green-600 text-white border-transparent shadow-sm"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <DocumentTextIcon className="h-4 w-4 flex-shrink-0" />
                  <span className="truncate">{t("settings:whatsappTitle")}</span>
                </button>
              </div>
            </div>

            {activeSection === "daily" && (
              <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-2xl p-4 shadow-lg">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                        <ClockIcon className="w-4 h-4" />
                      </div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {t("settings:dailyTargetTitle")}
                      </h2>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 max-w-xl">
                      {t("settings:dailyTargetDesc")}
                    </p>
                  </div>
                  <button
                    onClick={saveDailyHours}
                    disabled={savingHours}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 disabled:opacity-60"
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    {savingHours
                      ? t("settings:saving")
                      : t("settings:saveWorkHours")}
                  </button>
                </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                    <div className="grid grid-cols-[140px_1fr] gap-4 items-center">
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 text-center">
                        <div className="text-[11px] text-blue-700 font-semibold">
                          {t("settings:dailyTarget")}
                        </div>
                        <div className="text-2xl font-bold text-blue-900 mt-1">
                          {dailyTargetHours.toFixed(1)}
                          <span className="text-xs font-medium text-blue-600 ml-1.5">
                            {t("settings:hours")}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <input
                          type="range"
                          min={1}
                          max={14}
                          step={0.5}
                          value={dailyTargetHours}
                          onChange={(event) =>
                            setDailyTargetHours(Number(event.target.value))
                          }
                          className="w-full accent-blue-600"
                        />
                        <div className="flex items-center justify-between text-[11px] text-gray-500">
                          <span>1</span>
                          <span>7</span>
                          <span>14</span>
                        </div>
                        <input
                          type="number"
                          min={1}
                          max={24}
                          step={0.5}
                          value={dailyTargetHours}
                          onChange={(event) =>
                            setDailyTargetHours(Number(event.target.value))
                          }
                          className="w-28 px-3 py-2 border border-blue-200 rounded-lg text-sm font-semibold text-blue-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-[140px_1fr] gap-4 items-center">
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3 text-center">
                        <div className="text-[11px] text-amber-700 font-semibold">
                          {t("settings:overtimeMultiplier")}
                        </div>
                        <div className="text-2xl font-bold text-amber-900 mt-1">
                          {overtimeMultiplier.toFixed(2)}x
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-xs text-gray-500">
                          {t("settings:overtimeMultiplierDesc")}
                        </p>
                        <input
                          type="number"
                          min={1}
                          max={5}
                          step={0.1}
                          value={overtimeMultiplier}
                          onChange={(event) =>
                            setOvertimeMultiplier(Number(event.target.value))
                          }
                          className="w-28 px-3 py-2 border border-amber-200 rounded-lg text-sm font-semibold text-amber-700 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

            {activeSection === "calendar" && (
              <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-2xl p-6 shadow-lg">
                <div className="flex flex-wrap items-start justify-between gap-6">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-rose-100 rounded-xl text-rose-600">
                        <CalendarDaysIcon className="w-5 h-5" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {t("settings:calendarTitle")}
                      </h2>
                    </div>
                    <p className="text-gray-600 mt-2 max-w-xl">
                      {t("settings:calendarDesc")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSelectedYear((prev) => prev - 1)}
                      className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                    >
                      <ArrowLeftIcon className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      value={selectedYear}
                      onChange={(event) =>
                        setSelectedYear(Number(event.target.value))
                      }
                      className="w-24 px-3 py-2 border border-gray-200 rounded-lg text-center font-semibold"
                    />
                    <button
                      onClick={() => setSelectedYear((prev) => prev + 1)}
                      className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50"
                    >
                      <ArrowRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3 text-xs font-semibold">
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-rose-100 text-rose-700">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
                    {t("settings:holidayLabel")}
                  </span>
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700">
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                    {t("settings:weekendLabel")}
                  </span>
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-sky-100 text-sky-700">
                    <span className="w-2.5 h-2.5 rounded-full bg-sky-500" />
                    {t("settings:halfDayLabel")}
                  </span>
                  <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                    {t("settings:workdayLabel")}
                  </span>
                  <span className="text-gray-500">
                    {t("settings:calendarHint")}
                  </span>
                </div>

                <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {Array.from({ length: 12 }, (_, monthIndex) => {
                    const days = getMonthDays(selectedYear, monthIndex);
                    return (
                      <div
                        key={`${selectedYear}-${monthIndex}`}
                        className="border border-gray-100 rounded-2xl p-4 bg-white shadow-sm"
                      >
                        <div className="text-sm font-semibold text-gray-800 mb-3 capitalize">
                          {getMonthLabel(selectedYear, monthIndex)}
                        </div>
                        <div className="grid grid-cols-7 gap-1 text-[11px] text-gray-400 mb-2">
                          {["Pzt", "Sal", "?ar", "Per", "Cum", "Cmt", "Paz"].map(
                            (label) => (
                              <div key={label} className="text-center">
                                {label}
                              </div>
                            )
                          )}
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                          {days.map((day, index) => {
                            if (!day) {
                              return (
                                <div
                                  key={`${monthIndex}-empty-${index}`}
                                  className="h-8"
                                />
                              );
                            }

                            const dateKey = buildDateKey(
                              selectedYear,
                              monthIndex,
                              day
                            );
                            const weekend = isWeekendDay(
                              selectedYear,
                              monthIndex,
                              day
                            );
                            const isOverride =
                              calendarState.workdayOverrides.has(dateKey);
                            const isHalfDay =
                              calendarState.halfDays.has(dateKey);
                            const isHoliday =
                              !isOverride &&
                              !isHalfDay &&
                              (weekend || calendarState.holidays.has(dateKey));
                            const isWorkday = !isHoliday && !isHalfDay;

                            return (
                              <button
                                type="button"
                                key={dateKey}
                                onClick={() =>
                                  handleToggleDate(
                                    selectedYear,
                                    monthIndex,
                                    day
                                  )
                                }
                                className={`h-8 rounded-lg text-xs font-semibold transition-all ${
                                  isHalfDay
                                    ? "bg-sky-100 text-sky-700 hover:bg-sky-200"
                                    : isWorkday
                                    ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                    : weekend
                                    ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                                    : "bg-rose-100 text-rose-700 hover:bg-rose-200"
                                }`}
                                title={dateKey}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-6 flex items-center justify-between flex-wrap gap-4">
                  <div className="text-sm text-gray-600">
                    {t("settings:holidayCount", { count: workdayCount })}
                  </div>
                </div>
              </div>
            )}

            {activeSection === "hourly" && (
              <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-2xl p-6 shadow-lg">
                <div className="flex items-start justify-between flex-wrap gap-6">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-emerald-100 rounded-xl text-emerald-600">
                        <CurrencyDollarIcon className="w-5 h-5" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {t("settings:hourlyCostTitle")}
                      </h2>
                    </div>
                    <p className="text-gray-600 mt-2 max-w-2xl">
                      {t("settings:hourlyCostDesc")}
                    </p>
                  </div>
                </div>

                <div className="mt-6 rounded-xl border border-emerald-100 bg-emerald-50/60 p-4 flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-sm font-semibold text-emerald-800">
                      {t("settings:averageHourlyCostTitle")}
                    </div>
                    <p className="text-xs text-emerald-700 mt-1 max-w-2xl">
                      {t("settings:averageHourlyCostDesc")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={openAverageRatesModal}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-white text-emerald-700 text-sm font-semibold hover:bg-emerald-100"
                  >
                    <DocumentTextIcon className="w-4 h-4" />
                    {t("settings:hourlyCostPeriods")}
                  </button>
                </div>

                {hourlyCostsLoading ? (
                  <div className="mt-6 animate-pulse space-y-4">
                    <div className="h-10 bg-slate-200 rounded" />
                    <div className="h-10 bg-slate-200 rounded" />
                    <div className="h-10 bg-slate-200 rounded" />
                  </div>
                ) : hourlyCosts.length === 0 ? (
                  <div className="mt-6 text-sm text-gray-500">
                    {t("settings:hourlyCostEmpty")}
                  </div>
                ) : (
                  <div className="mt-6 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="text-xs uppercase tracking-wide text-gray-400">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">
                            {t("settings:hourlyCostPerson")}
                          </th>
                          <th className="px-4 py-3 text-left font-semibold">
                            {t("settings:hourlyCostRole")}
                          </th>
                        <th className="px-4 py-3 text-right font-semibold">
                          {t("settings:hourlyCostPeriods")}
                        </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                      {hourlyCosts.map((user) => (
                        <tr key={user.id} className="hover:bg-slate-50/60">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-xs text-gray-500">
                              @{user.username || "-"} - {user.email || "-"}
                            </div>
                          </td>
                              <td className="px-4 py-3 text-gray-600">
                                {getRoleLabel(user.role)}
                              </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                type="button"
                                onClick={() => openHourlyRatesModal(user)}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100"
                              >
                                <DocumentTextIcon className="w-4 h-4" />
                                {t("settings:hourlyCostPeriods")}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeSection === "taxes" && (
              <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-2xl p-4 shadow-lg">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-amber-100 rounded-lg text-amber-700">
                        <CurrencyDollarIcon className="w-4 h-4" />
                      </div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {t("settings:taxSettingsTitle")}
                      </h2>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 max-w-xl">
                      {t("settings:taxSettingsDesc")}
                    </p>
                  </div>
                  <button
                    onClick={saveTaxes}
                    disabled={savingTaxes}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold shadow-sm hover:bg-amber-700 disabled:opacity-60"
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    {savingTaxes ? t("settings:saving") : t("settings:saveTaxes")}
                  </button>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">
                      {t("settings:vatRate")}
                    </span>
                    <div className="mt-1 relative">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        value={vatRate}
                        onChange={(event) => setVatRate(Number(event.target.value))}
                        className="w-full px-3 py-2 pr-8 border border-amber-200 rounded-lg text-sm font-semibold text-amber-700 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      />
                      <span className="absolute inset-y-0 right-3 flex items-center text-sm text-amber-600">
                        %
                      </span>
                    </div>
                  </label>

                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">
                      {t("settings:corporateTaxRate")}
                    </span>
                    <div className="mt-1 relative">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={0.01}
                        value={corporateTaxRate}
                        onChange={(event) =>
                          setCorporateTaxRate(Number(event.target.value))
                        }
                        className="w-full px-3 py-2 pr-8 border border-amber-200 rounded-lg text-sm font-semibold text-amber-700 focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                      />
                      <span className="absolute inset-y-0 right-3 flex items-center text-sm text-amber-600">
                        %
                      </span>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {activeSection === "exchange" && (
              <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-2xl p-4 shadow-lg">
                <div className="flex items-start justify-between flex-wrap gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-violet-100 rounded-lg text-violet-700">
                        <DocumentTextIcon className="w-4 h-4" />
                      </div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {t("settings:exchangeRateTitle")}
                      </h2>
                    </div>
                    <p className="text-sm text-gray-600 mt-1 max-w-3xl">
                      {t("settings:exchangeRateDesc")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addDailyExchangeRateRow}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold shadow-sm hover:bg-violet-700"
                  >
                    <DocumentTextIcon className="w-4 h-4" />
                    {t("settings:exchangeRateAdd")}
                  </button>
                </div>

                <div className="mt-4 rounded-xl border border-violet-100 bg-violet-50/60 px-4 py-3 text-xs text-violet-800">
                  {t("settings:exchangeRateHint", {
                    month: selectedExchangeMonthLabel,
                    startDate: dailyExchangeRangeStart,
                    endDate: dailyExchangeRangeEnd,
                  })}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="font-medium">
                      {t("settings:exchangeRateMonth")}
                    </span>
                    <input
                      type="month"
                      value={selectedExchangeMonth}
                      max={currentExchangeMonthValue}
                      onChange={(event) =>
                        setSelectedExchangeMonth(
                          event.target.value || currentExchangeMonthValue
                        )
                      }
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedExchangeMonth((prev) =>
                        shiftMonthValue(prev, -1)
                      )
                    }
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-violet-200 hover:text-violet-700"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t("settings:exchangeRatePreviousMonth")}
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedExchangeMonth((prev) =>
                        shiftMonthValue(prev, 1)
                      )
                    }
                    disabled={selectedExchangeMonth === currentExchangeMonthValue}
                    className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:border-violet-200 hover:text-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {t("settings:exchangeRateNextMonth")}
                    <ArrowRightIcon className="h-4 w-4" />
                  </button>
                  <div className="text-sm text-gray-500">
                    {selectedExchangeMonthLabel}
                  </div>
                </div>

                {dailyExchangeRatesLoading ? (
                  <div className="mt-6 animate-pulse space-y-4">
                    <div className="h-10 bg-slate-200 rounded" />
                    <div className="h-10 bg-slate-200 rounded" />
                    <div className="h-10 bg-slate-200 rounded" />
                  </div>
                ) : dailyExchangeRates.length === 0 ? (
                  <div className="mt-6 text-sm text-gray-500">
                    {t("settings:exchangeRateEmpty")}
                  </div>
                ) : (
                  <div className="mt-6 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="text-xs uppercase tracking-wide text-gray-400">
                        <tr>
                          <th className="px-3 py-3 text-left font-semibold">
                            {t("settings:exchangeRateDate")}
                          </th>
                          <th className="px-3 py-3 text-left font-semibold">
                            {t("settings:exchangeRateBase")}
                          </th>
                          {visibleDailyExchangeRateCurrencies.map((currency) => (
                            <th
                              key={currency.code}
                              className="px-3 py-3 text-left font-semibold"
                            >
                              {currency.code}
                            </th>
                          ))}
                          <th className="px-3 py-3 text-left font-semibold">
                            {t("settings:exchangeRateMore")}
                          </th>
                          <th className="px-3 py-3 text-left font-semibold">
                            {t("settings:exchangeRateProvider")}
                          </th>
                          <th className="px-3 py-3 text-right font-semibold">
                            {t("settings:hourlyCostAction")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {dailyExchangeRates.map((row, index) => {
                          const isSaving =
                            savingDailyExchangeRateKey === row.rateDate;
                          const isDeleting =
                            deletingDailyExchangeRateKey === row.rateDate;

                          return (
                            <tr key={row.id ?? `daily-rate-${row.rateDate}-${index}`}>
                              <td className="px-3 py-2">
                                <input
                                  type="date"
                                  value={row.rateDate}
                                  onChange={(event) =>
                                    updateDailyExchangeRateDate(
                                      index,
                                      event.target.value
                                    )
                                  }
                                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                                />
                              </td>
                              <td className="px-3 py-2 font-semibold text-gray-700">
                                {row.baseCurrency || "TRY"}
                              </td>
                              {visibleDailyExchangeRateCurrencies.map((currency) => (
                                <td key={currency.code} className="px-3 py-2">
                                  <input
                                    type="number"
                                    min={0}
                                    step={0.0001}
                                    value={row.rates[currency.code] ?? ""}
                                    onChange={(event) =>
                                      updateDailyExchangeRateField(
                                        index,
                                        currency.code,
                                        event.target.value
                                      )
                                    }
                                    placeholder="0.0000"
                                    className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                                  />
                                </td>
                              ))}
                              <td className="px-3 py-2">
                                <button
                                  type="button"
                                  onClick={() => openDailyExchangeRateModal(index)}
                                  className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-100"
                                >
                                  <EllipsisHorizontalCircleIcon className="h-4 w-4" />
                                  <span>{t("settings:exchangeRateMore")}</span>
                                  {hiddenDailyExchangeRateCurrencies.length > 0 && (
                                    <span className="rounded-full bg-white px-2 py-0.5 text-xs text-sky-700">
                                      +{hiddenDailyExchangeRateCurrencies.length}
                                    </span>
                                  )}
                                </button>
                              </td>
                              <td className="px-3 py-2 text-xs text-gray-500">
                                {row.provider || "MANUAL"}
                              </td>
                              <td className="px-3 py-2">
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => saveDailyExchangeRate(row)}
                                    disabled={isSaving || isDeleting}
                                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-violet-200 bg-violet-50 text-violet-700 text-sm font-semibold hover:bg-violet-100 disabled:opacity-60"
                                  >
                                    <CheckCircleIcon className="w-4 h-4" />
                                    {isSaving
                                      ? t("settings:saving")
                                      : t("settings:exchangeRateSave")}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => deleteDailyExchangeRate(row)}
                                    disabled={isSaving || isDeleting}
                                    className="inline-flex items-center justify-center p-2 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-60"
                                    aria-label="Delete"
                                  >
                                    <XMarkIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeSection === "whatsapp" && (
              <div className="bg-white/80 backdrop-blur-sm border border-white/50 rounded-2xl p-6 shadow-lg">
                <div className="flex items-start justify-between flex-wrap gap-6">
                  <div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-xl text-green-600">
                        <DocumentTextIcon className="w-5 h-5" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {t("settings:whatsappTitle")}
                      </h2>
                    </div>
                    <p className="text-gray-600 mt-2 max-w-3xl">
                      {t("settings:whatsappDesc")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={sendWhatsAppTestMessage}
                      disabled={
                        savingWhatsAppBot ||
                        sendingWhatsAppReport ||
                        sendingWhatsAppTest
                      }
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-sky-200 bg-white text-sky-700 text-sm font-semibold shadow-sm hover:bg-sky-50 disabled:opacity-60"
                    >
                      <DocumentTextIcon className="w-4 h-4" />
                      {sendingWhatsAppTest
                        ? t("settings:whatsappTesting")
                        : t("settings:whatsappTest")}
                    </button>
                    <button
                      type="button"
                      onClick={resendWhatsAppReportNow}
                      disabled={
                        savingWhatsAppBot ||
                        sendingWhatsAppReport ||
                        sendingWhatsAppTest
                      }
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-green-200 bg-white text-green-700 text-sm font-semibold shadow-sm hover:bg-green-50 disabled:opacity-60"
                    >
                      <ArrowRightIcon className="w-4 h-4" />
                      {sendingWhatsAppReport
                        ? t("settings:whatsappResending")
                        : t("settings:whatsappResend")}
                    </button>
                    <button
                      type="button"
                      onClick={saveWhatsAppBotSettings}
                      disabled={
                        savingWhatsAppBot ||
                        sendingWhatsAppReport ||
                        sendingWhatsAppTest
                      }
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold shadow-sm hover:bg-green-700 disabled:opacity-60"
                    >
                      <CheckCircleIcon className="w-4 h-4" />
                      {savingWhatsAppBot
                        ? t("settings:saving")
                        : t("settings:whatsappSave")}
                    </button>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-6">
                  <div className="space-y-5">
                    <div className="rounded-2xl border border-green-100 bg-green-50/60 p-4">
                      <label className="flex items-center justify-between gap-4">
                        <div>
                          <div className="text-sm font-semibold text-green-900">
                            {t("settings:whatsappEnabled")}
                          </div>
                          <p className="text-xs text-green-700 mt-1">
                            {t("settings:whatsappEnabledDesc")}
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          checked={whatsAppBotEnabled}
                          onChange={(event) =>
                            setWhatsAppBotEnabled(event.target.checked)
                          }
                          className="h-5 w-5 rounded border-slate-300 text-green-600 focus:ring-green-500"
                        />
                      </label>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        {t("settings:whatsappRecipients")}
                      </label>
                      <textarea
                        rows={5}
                        value={whatsAppRecipientsInput}
                        onChange={(event) =>
                          setWhatsAppRecipientsInput(event.target.value)
                        }
                        placeholder={t("settings:whatsappRecipientsPlaceholder")}
                        className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <p className="mt-2 text-xs text-slate-500">
                        {t("settings:whatsappRecipientsHint")}
                      </p>
                    </div>

                    <div className="max-w-xs">
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        {t("settings:whatsappSendTime")}
                      </label>
                      <input
                        type="time"
                        value={whatsAppSendTime}
                        onChange={(event) => setWhatsAppSendTime(event.target.value)}
                        className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      <p className="mt-2 text-xs text-slate-500">
                        {t("settings:whatsappSendTimeHint")}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <label className="block text-sm font-semibold text-slate-700">
                          {t("settings:whatsappColumns")}
                        </label>
                        <span className="text-xs text-slate-500">
                          {selectedWhatsAppColumns.length}{" "}
                          {t("settings:whatsappColumnsSelected")}
                        </span>
                      </div>
                      <div className="rounded-2xl border border-slate-200 p-4 bg-slate-50/70 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {WHATSAPP_ACTIVITY_COLUMN_OPTIONS.map((option) => (
                          <label
                            key={option.id}
                            className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                          >
                            <input
                              type="checkbox"
                              checked={selectedWhatsAppColumns.includes(option.id)}
                              onChange={() => toggleWhatsAppColumn(option.id)}
                              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-green-600 focus:ring-green-500"
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-slate-500">
                        {t("settings:whatsappColumnsHint")}
                      </p>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <div className="text-sm font-semibold text-slate-800">
                        {t("settings:whatsappPreviewTitle")}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {t("settings:whatsappPreviewDesc")}
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {selectedWhatsAppColumnLabels.length > 0 ? (
                          selectedWhatsAppColumnLabels.map((label) => (
                            <span
                              key={label}
                              className="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700"
                            >
                              {label}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-slate-500">
                            {t("settings:whatsappPreviewEmpty")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

        )}
      </div>

      {activeDailyExchangeRateRow && dailyExchangeRateModalIndex !== null && (
        <div className="fixed inset-0 z-50 overflow-y-auto px-4 py-4 sm:py-6">
          <button
            type="button"
            onClick={closeDailyExchangeRateModal}
            disabled={savingDailyExchangeRateKey === activeDailyExchangeRateRow.rateDate}
            className="absolute inset-0 bg-black/40"
            aria-label="Close"
          />
          <div className="relative mx-auto w-full max-w-6xl max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] rounded-2xl border border-white/50 bg-white shadow-2xl overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-sky-50 to-violet-50">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-sky-800">
                    {t("settings:exchangeRateMoreTitle")}
                  </div>
                  <div className="text-xs text-sky-700 mt-1">
                    {t("settings:exchangeRateMoreDesc", {
                      rateDate: activeDailyExchangeRateRow.rateDate,
                    })}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                    <span>
                      {t("settings:exchangeRateDate")}:{" "}
                      <strong>{activeDailyExchangeRateRow.rateDate}</strong>
                    </span>
                    <span>
                      {t("settings:exchangeRateBase")}:{" "}
                      <strong>{activeDailyExchangeRateRow.baseCurrency || "TRY"}</strong>
                    </span>
                    <span>
                      {t("settings:exchangeRateProvider")}:{" "}
                      <strong>{activeDailyExchangeRateRow.provider || "MANUAL"}</strong>
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeDailyExchangeRateModal}
                  disabled={savingDailyExchangeRateKey === activeDailyExchangeRateRow.rateDate}
                  className="p-2 rounded-lg bg-white/80 border border-sky-100 text-gray-600 hover:bg-white"
                  aria-label={t("settings:exchangeRateMoreClose")}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-5 py-4 backdrop-blur">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <label className="relative block w-full lg:max-w-sm">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="search"
                      value={dailyExchangeRateModalSearch}
                      onChange={(event) =>
                        setDailyExchangeRateModalSearch(event.target.value)
                      }
                      placeholder={t("settings:exchangeRateMoreSearchPlaceholder")}
                      className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm focus:border-violet-500 focus:ring-2 focus:ring-violet-500"
                    />
                  </label>
                  <div className="text-xs font-medium text-gray-500">
                    {t("settings:exchangeRateMoreCount", {
                      visible: filteredDailyExchangeRateCurrencyOptions.length,
                      total: dailyExchangeRateCurrencyOptions.length,
                    })}
                  </div>
                </div>
              </div>

              <div className="p-5">
                {filteredDailyExchangeRateCurrencyOptions.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
                    {t("settings:exchangeRateMoreEmpty")}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {filteredDailyExchangeRateCurrencyOptions.map((currency) => (
                      <label
                        key={currency.code}
                        className="rounded-xl border border-gray-200 bg-gray-50/60 px-4 py-3"
                      >
                        <div className="text-xs font-semibold text-gray-500">
                          {currency.modalLabel}
                        </div>
                        <input
                          type="number"
                          min={0}
                          step={0.0001}
                          value={activeDailyExchangeRateRow.rates[currency.code] ?? ""}
                          onChange={(event) =>
                            updateDailyExchangeRateField(
                              dailyExchangeRateModalIndex,
                              currency.code,
                              event.target.value
                            )
                          }
                          placeholder="0.0000"
                          className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        />
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-gray-100 bg-white px-5 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-xs text-gray-500">
                {t("settings:exchangeRateMoreHint")}
                </div>
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeDailyExchangeRateModal}
                    className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50"
                  >
                    {t("settings:exchangeRateMoreClose")}
                  </button>
                  <button
                    type="button"
                    onClick={saveDailyExchangeRateFromModal}
                    disabled={savingDailyExchangeRateKey === activeDailyExchangeRateRow.rateDate}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold shadow-sm hover:bg-violet-700 disabled:opacity-60"
                  >
                    <CheckCircleIcon className="w-4 h-4" />
                    {savingDailyExchangeRateKey === activeDailyExchangeRateRow.rateDate
                      ? t("settings:saving")
                      : t("settings:exchangeRateSave")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {hourlyRateModalUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <button
            type="button"
            onClick={() => {
              if (savingHourlyRates) return;
              closeHourlyRatesModal();
            }}
            disabled={savingHourlyRates}
            className="absolute inset-0 bg-black/40"
            aria-label="Close"
          />
          <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-white/50 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-emerald-800">
                    {t("settings:hourlyCostPeriodsTitle")}
                  </div>
                  <div className="text-xs text-emerald-700 mt-1">
                    {hourlyRateModalUser.firstName} {hourlyRateModalUser.lastName} (
                    {getRoleLabel(hourlyRateModalUser.role)})
                  </div>
                  <div className="text-xs text-gray-600 mt-2 max-w-3xl">
                    {t("settings:hourlyCostPeriodsDesc")}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (savingHourlyRates) return;
                    closeHourlyRatesModal();
                  }}
                  disabled={savingHourlyRates}
                  className="p-2 rounded-lg bg-white/80 border border-emerald-100 text-gray-600 hover:bg-white"
                  aria-label={t("settings:hourlyCostPeriodsClose")}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-5">
              {hourlyRatesLoading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-10 bg-slate-200 rounded" />
                  <div className="h-10 bg-slate-200 rounded" />
                  <div className="h-10 bg-slate-200 rounded" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-xs text-gray-500">
                    {t("settings:hourlyCostPeriodsEndEmptyHint")}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="text-xs uppercase tracking-wide text-gray-400">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">
                            {t("settings:hourlyCostPeriodsStart")}
                          </th>
                          <th className="px-3 py-2 text-left font-semibold">
                            {t("settings:hourlyCostPeriodsEnd")}
                          </th>
                          <th className="px-3 py-2 text-left font-semibold">
                            {t("settings:hourlyCostValue")}
                          </th>
                          <th className="px-3 py-2 text-left font-semibold">
                            {t("settings:hourlyCostCurrency")}
                          </th>
                          <th className="px-3 py-2 text-right font-semibold">
                            {t("settings:hourlyCostAction")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {hourlyRates.length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-3 py-4 text-sm text-gray-500"
                            >
                              {t("settings:hourlyCostPeriodsEmpty")}
                            </td>
                          </tr>
                        ) : (
                          hourlyRates.map((row, idx) => (
                            <tr key={row.id ?? `new-${idx}`}>
                              <td className="px-3 py-2">
                                <input
                                  type="date"
                                  value={row.startDate || ""}
                                  onChange={(e) =>
                                    updateHourlyRateField(
                                      idx,
                                      "startDate",
                                      e.target.value
                                    )
                                  }
                                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="date"
                                  value={row.endDate ?? ""}
                                  onChange={(e) =>
                                    updateHourlyRateField(
                                      idx,
                                      "endDate",
                                      e.target.value === "" ? null : e.target.value
                                    )
                                  }
                                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={row.hourlyCost ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    updateHourlyRateField(
                                      idx,
                                      "hourlyCost",
                                      v === "" ? null : Number(v)
                                    );
                                  }}
                                  placeholder="0.00"
                                  className="w-32 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={row.currency || "TRY"}
                                  onChange={(e) =>
                                    updateHourlyRateField(idx, "currency", e.target.value)
                                  }
                                  className="w-36 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                >
                                  {HOURLY_COST_CURRENCIES.map((option) => (
                                    <option key={option.code} value={option.code}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => removeHourlyRateRow(idx)}
                                  className="inline-flex items-center justify-center p-2 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                  aria-label="Remove"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 bg-white flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={addHourlyRateRow}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100"
              >
                <DocumentTextIcon className="w-4 h-4" />
                {t("settings:hourlyCostPeriodsAdd")}
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeHourlyRatesModal}
                  className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50"
                >
                  {t("settings:hourlyCostPeriodsClose")}
                </button>
                <button
                  type="button"
                  onClick={saveHourlyRates}
                  disabled={savingHourlyRates}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  {savingHourlyRates
                    ? t("settings:saving")
                    : t("settings:hourlyCostPeriodsSave")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {averageRateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
          <button
            type="button"
            onClick={() => {
              if (savingAverageHourlyRates) return;
              closeAverageRatesModal();
            }}
            disabled={savingAverageHourlyRates}
            className="absolute inset-0 bg-black/40"
            aria-label="Close"
          />
          <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-white/50 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-emerald-50 to-teal-50">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-sm font-semibold text-emerald-800">
                    {t("settings:averageHourlyCostPeriodsTitle")}
                  </div>
                  <div className="text-xs text-gray-600 mt-2 max-w-3xl">
                    {t("settings:averageHourlyCostPeriodsDesc")}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (savingAverageHourlyRates) return;
                    closeAverageRatesModal();
                  }}
                  disabled={savingAverageHourlyRates}
                  className="p-2 rounded-lg bg-white/80 border border-emerald-100 text-gray-600 hover:bg-white"
                  aria-label={t("settings:hourlyCostPeriodsClose")}
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-5">
              {averageHourlyRatesLoading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-10 bg-slate-200 rounded" />
                  <div className="h-10 bg-slate-200 rounded" />
                  <div className="h-10 bg-slate-200 rounded" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="text-xs text-gray-500">
                    {t("settings:hourlyCostPeriodsEndEmptyHint")}
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="text-xs uppercase tracking-wide text-gray-400">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">
                            {t("settings:hourlyCostPeriodsStart")}
                          </th>
                          <th className="px-3 py-2 text-left font-semibold">
                            {t("settings:hourlyCostPeriodsEnd")}
                          </th>
                          <th className="px-3 py-2 text-left font-semibold">
                            {t("settings:hourlyCostValue")}
                          </th>
                          <th className="px-3 py-2 text-left font-semibold">
                            {t("settings:hourlyCostCurrency")}
                          </th>
                          <th className="px-3 py-2 text-right font-semibold">
                            {t("settings:hourlyCostAction")}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {averageHourlyRates.length === 0 ? (
                          <tr>
                            <td
                              colSpan={5}
                              className="px-3 py-4 text-sm text-gray-500"
                            >
                              {t("settings:hourlyCostPeriodsEmpty")}
                            </td>
                          </tr>
                        ) : (
                          averageHourlyRates.map((row, idx) => (
                            <tr key={row.id ?? `new-average-${idx}`}>
                              <td className="px-3 py-2">
                                <input
                                  type="date"
                                  value={row.startDate || ""}
                                  onChange={(e) =>
                                    updateAverageHourlyRateField(
                                      idx,
                                      "startDate",
                                      e.target.value
                                    )
                                  }
                                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="date"
                                  value={row.endDate ?? ""}
                                  onChange={(e) =>
                                    updateAverageHourlyRateField(
                                      idx,
                                      "endDate",
                                      e.target.value === "" ? null : e.target.value
                                    )
                                  }
                                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  type="number"
                                  min={0}
                                  step={0.01}
                                  value={row.hourlyCost ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    updateAverageHourlyRateField(
                                      idx,
                                      "hourlyCost",
                                      v === "" ? null : Number(v)
                                    );
                                  }}
                                  placeholder="0.00"
                                  className="w-32 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  value={row.currency || "TRY"}
                                  onChange={(e) =>
                                    updateAverageHourlyRateField(
                                      idx,
                                      "currency",
                                      e.target.value
                                    )
                                  }
                                  className="w-36 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                >
                                  {HOURLY_COST_CURRENCIES.map((option) => (
                                    <option key={option.code} value={option.code}>
                                      {option.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => removeAverageHourlyRateRow(idx)}
                                  className="inline-flex items-center justify-center p-2 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                                  aria-label="Remove"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 bg-white flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={addAverageHourlyRateRow}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm font-semibold hover:bg-emerald-100"
              >
                <DocumentTextIcon className="w-4 h-4" />
                {t("settings:hourlyCostPeriodsAdd")}
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeAverageRatesModal}
                  className="px-4 py-2 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50"
                >
                  {t("settings:hourlyCostPeriodsClose")}
                </button>
                <button
                  type="button"
                  onClick={saveAverageHourlyRates}
                  disabled={savingAverageHourlyRates}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  {savingAverageHourlyRates
                    ? t("settings:saving")
                    : t("settings:hourlyCostPeriodsSave")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};



